import { MouseEventHandler, useCallback, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Box, FormControlLabel, Link, Typography } from "@mui/material";

import { StyledCheckbox } from "@covertree/ui";
import { LegalDocumentLink } from "@covertree/ui/link";
import { createDownloadableLink, Toast } from "@covertree/utils";
import {
  selectPolicy,
  useNewBusinessSelector,
} from "@covertree/new-business-redux";

import { useCheckout } from "../../../CheckoutProvider";
import { ConsentsFormState } from "../../types";

export function ConsumerConsents() {
  const { anythingLoading, documents, getQuoteDocuments } = useCheckout();
  const policy = useNewBusinessSelector(selectPolicy);

  const policyholderName = useMemo(() => {
    if (!policy?.policyholder) {
      return "";
    }

    return `${policy.policyholder.firstName} ${policy.policyholder.lastName}`;
  }, [policy?.policyholder]);

  const onPolicyApplicationDownload: MouseEventHandler<HTMLAnchorElement> =
    useCallback(
      async (event) => {
        event.preventDefault();

        if (anythingLoading) {
          return;
        }

        if (!documents) {
          Toast.showInfo(
            "Preparing Policy Application. It will automatically download when ready."
          );
          const fetchedDocuments = await getQuoteDocuments();
          if (fetchedDocuments) {
            createDownloadableLink(fetchedDocuments.applicationDocumentUrl);
          }
        } else {
          createDownloadableLink(documents.applicationDocumentUrl);
        }
      },
      [documents, getQuoteDocuments, anythingLoading]
    );

  const { control } = useFormContext<ConsentsFormState>();

  return (
    <>
      <Typography>Electronically Sign Your Application</Typography>
      <Typography variant="subtitle2" my={2} fontWeight={400}>
        By clicking the checkbox and the &#34;Purchase Your Policy&#34; button
        below, you are electronically signing your{" "}
        <Link
          href="#"
          color="secondary"
          underline="hover"
          onClick={onPolicyApplicationDownload}>
          Policy Application
        </Link>{" "}
        and consent to the{" "}
        <LegalDocumentLink document="PrivacyNotice" underline="hover" />,{" "}
        <LegalDocumentLink document="ElectronicDelivery" underline="hover" />,
        and{" "}
        <LegalDocumentLink
          document="RecurringPaymentNotice"
          underline="hover"
        />
        .
      </Typography>
      <Controller
        render={({ field }) => (
          <Box
            sx={(theme) => ({
              backgroundColor: theme.palette.action.selected,
              borderRadius: theme.spacing(1.5),
            })}>
            <FormControlLabel
              sx={{
                alignItems: "flex-start",
                mx: 0,
                p: 2,
              }}
              disabled={anythingLoading}
              control={
                <StyledCheckbox
                  squareIcon={true}
                  data-testid="accept-regulations-checkbox"
                  checked={!!field.value}
                  onChange={field.onChange}
                  disabled={anythingLoading}
                />
              }
              label={
                <Typography ml={2} variant="subtitle2" fontWeight={400}>
                  By clicking this checkbox, I, {policyholderName}, hereby sign
                  the{" "}
                  <Link
                    href="#"
                    onClick={onPolicyApplicationDownload}
                    color="secondary"
                    underline="hover"
                    target="_blank">
                    Policy Application,
                  </Link>{" "}
                  and acknowledge that I agree to Covertree&apos;s{" "}
                  <LegalDocumentLink
                    document="TermsOfService"
                    underline="hover"
                  />{" "}
                  and{" "}
                  <LegalDocumentLink
                    document="PrivacyPolicy"
                    underline="hover"
                  />
                </Typography>
              }
            />
          </Box>
        )}
        name="customerESign"
        control={control}
      />
    </>
  );
}
